import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Header } from '@/widgets/header'
import { Footer } from '@/widgets/footer'
import { designTokens } from '@/shared/lib/design-tokens'
import {
  clearOnboardingBillingPreview,
  readOnboardingBillingPreview,
} from '@/shared/lib/onboarding-billing-preview'
import bgOnboardingNew from '@/shared/assets/onboarding/bg_onboarding_new.png'
import group2087328982 from '@/shared/assets/onboarding/group_2087328982.png'
import { routes } from '@/shared/config'
import { loadBillingSummary } from '@/features/billing'
import { OnboardingPageDesktop } from './onboarding-page-desktop'
import { OnboardingPageMobile } from './onboarding-page-mobile'

const supportBotUrl = 'https://t.me/ps24supportbot'
const rubFormatter = new Intl.NumberFormat('ru-RU')
const dateFormatter = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
})

const openSupportBot = () => window.open(supportBotUrl, '_blank', 'noopener,noreferrer')

const arePreviewAndSummaryEqual = (
  preview: { nextTransactionAt: string; nextChargeAmountCents: number } | null,
  summary: { nextTransactionAt: Date | null; nextChargeAmountCents: number | null }
) => {
  if (!preview || !summary.nextTransactionAt || summary.nextChargeAmountCents == null) {
    return false
  }

  return (
    new Date(preview.nextTransactionAt).getTime() === summary.nextTransactionAt.getTime() &&
    preview.nextChargeAmountCents === summary.nextChargeAmountCents
  )
}

export function OnboardingPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [billingPreview] = useState(() => readOnboardingBillingPreview())
  const navigationPreview = (
    location.state as {
      onboardingBillingPreview?: {
        nextTransactionAt: string
        nextChargeAmountCents: number
      }
    } | null
  )?.onboardingBillingPreview
  const billingSummaryQuery = useQuery({
    queryKey: ['billing', 'summary'],
    queryFn: loadBillingSummary,
    staleTime: 30_000,
  })
  const desktopTokens = designTokens.components.onboardingSuccess.desktop
  const collageOffsetValue = Number.parseFloat(desktopTokens.collageOffsetY)
  const collageOffsetUnit = desktopTokens.collageOffsetY.replace(`${collageOffsetValue}`, '') || '%'
  const collageOffsetCompensation =
    collageOffsetValue < 0
      ? `${Math.abs(collageOffsetValue)}${collageOffsetUnit}`
      : `0${collageOffsetUnit}`
  const collageRenderHeight = `calc(${desktopTokens.collageHeightScale} + ${collageOffsetCompensation})`
  const summaryTransactionAt = billingSummaryQuery.data?.nextTransactionAt ?? null
  const summaryChargeAmount = billingSummaryQuery.data?.nextChargeAmountCents ?? null
  const effectivePreview = navigationPreview ?? billingPreview
  const previewTransactionAt = effectivePreview?.nextTransactionAt
    ? new Date(effectivePreview.nextTransactionAt)
    : null
  const previewChargeAmount = effectivePreview?.nextChargeAmountCents ?? null
  const hasPreview = Boolean(effectivePreview)
  const effectiveTransactionAt = hasPreview
    ? previewTransactionAt
    : summaryTransactionAt && summaryChargeAmount != null
      ? summaryTransactionAt
      : null
  const effectiveChargeAmount = hasPreview
    ? previewChargeAmount
    : summaryTransactionAt && summaryChargeAmount != null
      ? summaryChargeAmount
      : null

  useEffect(() => {
    if (
      arePreviewAndSummaryEqual(effectivePreview, {
        nextTransactionAt: summaryTransactionAt,
        nextChargeAmountCents: summaryChargeAmount,
      })
    ) {
      clearOnboardingBillingPreview()
    }
  }, [effectivePreview, summaryChargeAmount, summaryTransactionAt])

  const renewalSummaryLine =
    effectiveTransactionAt && effectiveChargeAmount != null
      ? `${dateFormatter.format(effectiveTransactionAt)} подписка будет продлена за ${rubFormatter.format(effectiveChargeAmount)} ₽`
      : 'Информацию о следующем списании можно увидеть в личном кабинете.'
  const reminderLine = 'Мы напомним о списании за 7 дней, отправив письмо на ваш email.'

  const handleContinue = () => {
    clearOnboardingBillingPreview()
    navigate(routes.videos)
  }

  return (
    <section
      className="relative flex min-h-screen w-full flex-col overflow-hidden"
      style={{
        backgroundColor: designTokens.colors.neutral.ink,
        backgroundImage: `url(${bgOnboardingNew})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
      }}
    >
      <div
        className="pointer-events-none absolute inset-y-0 hidden xl:block"
        style={{
          width: desktopTokens.collageWidth,
          right: `max(0px, calc((100vw - ${desktopTokens.frameWidth}) / 2))`,
        }}
      >
        <div
          className="absolute inset-x-0"
          style={{
            top: desktopTokens.collageOffsetY,
            height: collageRenderHeight,
            backgroundImage: `url(${group2087328982})`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'top center',
            backgroundSize: 'cover',
          }}
        />
      </div>
      <Header
        className="relative z-10 hidden xl:flex"
        logoColor={designTokens.colors.text.inverse}
      />
      <main className="relative z-10 flex w-full flex-1 flex-col items-center">
        <OnboardingPageDesktop
          onContinue={handleContinue}
          renewalSummaryLine={renewalSummaryLine}
          reminderLine={reminderLine}
        />
        <OnboardingPageMobile
          onContinue={handleContinue}
          renewalSummaryLine={renewalSummaryLine}
          reminderLine={reminderLine}
        />
      </main>
      <div className="relative z-10 hidden w-full xl:flex">
        <div className="mx-auto w-full" style={{ maxWidth: desktopTokens.frameWidth }}>
          <Footer onRightClick={openSupportBot} />
        </div>
      </div>
    </section>
  )
}
